import { useEffect } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import './mask.css';

/* CarmaCircle Mask System 1.0 — Eine Maske. Eine Entscheidung. */

export const FocusedJourneyLayout = ({ children }) => {
  useEffect(() => {
    const header = document.querySelector('header');
    if (header) { header.setAttribute('inert', ''); header.setAttribute('aria-hidden', 'true'); }
    document.body.classList.add('cc-focused-journey');
    return () => {
      if (header) { header.removeAttribute('inert'); header.removeAttribute('aria-hidden'); }
      document.body.classList.remove('cc-focused-journey');
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden" style={{ background: 'var(--bg, #f7f5f1)' }} data-testid="focused-journey">
      {children}
    </div>
  );
};

export const MaskContainer = ({ children, bgWord, bgPosition, bgShift, align = 'left', maskKey, ...rest }) => (
  <div key={maskKey} className="relative min-h-screen flex flex-col" data-testid="mask-container" {...rest}>
    {bgWord && <BackgroundEmotionTypography word={bgWord} position={bgPosition} shift={bgShift} />}
    <div className={`relative z-10 flex-1 flex flex-col justify-center w-full max-w-xl mx-auto px-6 sm:px-8 py-24 ${align === 'center' ? 'items-center text-center' : ''}`}>
      {children}
    </div>
  </div>
);

export const BackgroundEmotionTypography = ({ word, position = 'center', shift = '-6%' }) => (
  <div
    className={`mask-bg-word ${position === 'top' ? 'mask-bg-top' : position === 'bottom' ? 'mask-bg-bottom' : ''}`}
    aria-hidden="true"
    data-testid="mask-bg-word"
  >
    <span style={{ transform: `translateX(${shift})` }}>{word}</span>
  </div>
);

export const MaskContext = ({ children }) => (
  <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-6" data-testid="mask-context">{children}</p>
);

export const MaskHeadline = ({ children }) => (
  <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl lg:text-5xl text-[var(--text)] mb-10" data-testid="mask-headline">{children}</h1>
);

/* Mobile: programmatischer autoFocus öffnet keine Tastatur und blockiert den
   anschließenden Tap-Fokus (iOS/Android "stuck focus"). Daher nur bei feinem Pointer. */
const hasFinePointer = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: fine)').matches;

const retriggerFocus = (e) => {
  const el = e.currentTarget;
  if (document.activeElement === el) {
    el.blur();
    setTimeout(() => el.focus(), 0);
  }
};

export const MaskInput = ({ autoFocus, ...props }) => (
  <input
    {...props}
    autoFocus={autoFocus && hasFinePointer()}
    onTouchEnd={retriggerFocus}
    className={`mask-enter mask-enter-delay-2 mask-input text-xl sm:text-2xl py-3 text-[var(--text)] ${props.className || ''}`}
  />
);

export const MaskTextarea = ({ autoFocus, ...props }) => (
  <textarea
    {...props}
    autoFocus={autoFocus && hasFinePointer()}
    onTouchEnd={retriggerFocus}
    className={`mask-enter mask-enter-delay-2 mask-textarea text-lg sm:text-xl text-[var(--text)] min-h-[38vh] ${props.className || ''}`}
  />
);

export const CharCounter = ({ value, max }) => {
  const len = (value || '').length;
  if (len < max * 0.9) return null;
  return (
    <p className="text-xs text-[var(--muted)] mt-2 text-right tabular-nums" data-testid="char-counter">
      {len.toLocaleString()} / {max.toLocaleString()}
    </p>
  );
};

export const PrimaryAction = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className="mask-enter mask-enter-delay-3 mask-primary rounded-full px-8 py-3.5 text-[15px] font-normal w-full sm:w-auto min-h-[50px]"
    style={{ background: 'var(--text, #1c2a3a)', color: 'var(--bg, #f7f5f1)' }}
  >
    {children}
  </button>
);

export const SecondaryAction = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className="mask-enter mask-enter-delay-3 mask-secondary text-sm text-[var(--muted)] hover:text-[var(--text)] py-3 px-2 transition-colors"
  >
    {children}
  </button>
);

export const MaskBack = (props) => (
  <button
    type="button"
    aria-label="Zurück"
    {...props}
    className="mask-back fixed top-5 left-5 z-20 w-11 h-11 flex items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] hover:bg-black/5 transition-colors"
  >
    <ArrowLeft size={20} strokeWidth={1.5} />
  </button>
);

export const MaskPageBack = (props) => (
  <button
    type="button"
    aria-label="Zurück"
    {...props}
    className="-ml-3 mb-8 w-11 h-11 flex items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] hover:bg-black/5 transition-colors"
  >
    <ArrowLeft size={20} strokeWidth={1.5} />
  </button>
);

export const MaskClose = (props) => (
  <button
    type="button"
    aria-label="Schließen"
    {...props}
    className="mask-close fixed top-5 right-5 z-20 w-11 h-11 flex items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] hover:bg-black/5 transition-colors"
  >
    <X size={20} strokeWidth={1.5} />
  </button>
);
